import { isApolloError } from '@apollo/client';
import React, { useEffect, useRef, useState } from 'react';
import {
  Image,
  View,
  ActivityIndicator,
  Platform,
  StyleSheet,
} from 'react-native';
import Video, {
  OnLoadData,
  OnProgressData,
  OnBufferData,
} from 'react-native-video';

import { Box } from '@components/Restyle';
import { useDocumentsByClientIdsLazyQuery } from '@graphql/generated';
import { useGetDocumentsByClientIdsQuery } from '@hooks/useGetDocumentsByClientIdsQuery';
import { AsyncStorage } from '@utils/storage';

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

const inFlight = new Map<string, Promise<string>>();

type CachedVideoPropsType = {
  thumbnail?: string;
  clientId?: string;
  shouldPause?: boolean;
  screenWidth?: number;
  screenHeight?: number;
  videoRefs?: React.RefObject<{
    [p: string]: HTMLVideoElement | null;
  }>;
  url?: string;
};

export const CachedVideo: React.FC<CachedVideoPropsType> = ({
  clientId,
  thumbnail = '',
  shouldPause = false,
  screenWidth,
  screenHeight,
  videoRefs,
  url,
}) => {
  const localRef = useRef<HTMLVideoElement | null>(null);
  const { batchIndividualRequest, isRequestInFlight } =
    useGetDocumentsByClientIdsQuery();
  const [documentsByClientIdsQuery] = useDocumentsByClientIdsLazyQuery();

  const inflightRef = useRef(isRequestInFlight);
  const batchRef = useRef(batchIndividualRequest);
  const docsQueryRef = useRef(documentsByClientIdsQuery);

  const [videoUrl, setVideoUrl] = useState(url ?? '');
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 3;

  const [isVideoReady, setIsVideoReady] = useState(false);

  const [isBuffering, setIsBuffering] = useState(false);
  const [_currentTime, setCurrentTime] = useState(0);
  const [_duration, setDuration] = useState(0);

  const [sourceKey, setSourceKey] = useState(clientId);

  const video = useRef<Video>(null);

  // Keep refs updated
  useEffect(() => {
    batchRef.current = batchIndividualRequest;
  }, [batchIndividualRequest]);
  useEffect(() => {
    inflightRef.current = isRequestInFlight;
  }, [isRequestInFlight]);
  useEffect(() => {
    docsQueryRef.current = documentsByClientIdsQuery;
  }, [documentsByClientIdsQuery]);

  // Handle pause and store ref
  useEffect(() => {
    if (videoRefs && clientId) {
      videoRefs.current[clientId] = localRef.current;
    }
    if (localRef.current && shouldPause) {
      localRef.current.pause();
    }
  }, [clientId, shouldPause, videoRefs]);

  // Fetch and cache video URL
  useEffect(() => {
    if (videoUrl || !clientId) return;

    const ac = new AbortController();

    (async () => {
      const cached = await AsyncStorage.getItem(clientId);
      if (cached) {
        setVideoUrl(cached);
        return;
      }

      if (inflightRef.current(clientId)) {
        const started = Date.now();
        while (Date.now() - started < 1000 && inflightRef.current(clientId)) {
          await sleep(200);
          const probe = await AsyncStorage.getItem(clientId);
          if (probe) {
            setVideoUrl(probe);
            return;
          }
        }
      }

      try {
        batchRef.current?.(clientId);
      } catch (e) {
        console.log('[CachedVideo] batch request error →', e);
      }

      const started = Date.now();
      for (let i = 0; i < 20; i++) {
        const probe = await AsyncStorage.getItem(clientId);
        if (probe) {
          setVideoUrl(probe);
          return;
        }
        if (Date.now() - started > 4000) break;
        await sleep(200);
      }

      const key = `doc:${clientId}`;
      let p = inFlight.get(key);
      if (!p) {
        p = (async () => {
          for (let attempt = 0; attempt < 3; attempt++) {
            try {
              const rtn: any = await docsQueryRef.current({
                variables: { clientIds: [clientId] },
                context: { fetchOptions: { signal: ac.signal } },
              });
              const url = rtn.data?.documentsByClientIds?.[0]?.file?.url ?? '';
              if (!url) {
                console.warn('[CachedVideo] File URL not found');
                return '';
              }
              await AsyncStorage.setItem(clientId, url);
              return url;
            } catch (err: any) {
              if (err?.name === 'AbortError') throw err;
              const isDocumentsNotFoundError =
                isApolloError(err as Error) &&
                (err as any).graphQLErrors?.some(
                  ({ message, path }: any) =>
                    message?.includes('Documents not found') &&
                    path?.includes('documentsByClientIds')
                ) === true;
              if (isDocumentsNotFoundError) return '';
              if (attempt < 2) {
                await sleep(500 * (attempt + 1));
                continue;
              }
              throw err;
            }
          }
          return '';
        })();
        inFlight.set(key, p);
        p.finally(() => {
          if (inFlight.get(key) === p) inFlight.delete(key);
        });
      }

      try {
        const fresh = await p;
        if (fresh) setVideoUrl(fresh);
      } catch (e) {
        console.log('[CachedVideo] Error fetching video URL:', e);
      }
    })();

    return () => ac.abort();
  }, [clientId]);

  // --- 🔁 Video retry logic on error ---
  const handleVideoError = (
    e: React.SyntheticEvent<HTMLVideoElement, Event>
  ) => {
    const videoEl = e.currentTarget;

    // Offline case
    if (!navigator.onLine) {
      console.warn(
        '[CachedVideo] Network offline, waiting for reconnection...'
      );
      window.addEventListener(
        'online',
        () => {
          console.log('[CachedVideo] Network restored, reloading video...');
          videoEl.load();
          videoEl.play().catch(() => {});
        },
        { once: true }
      );
      return;
    }

    // Limit retry attempts
    if (retryCount >= MAX_RETRIES) {
      console.error('[CachedVideo] Video failed after multiple retries.');
      return;
    }

    console.warn(
      `[CachedVideo] Error loading video, retrying ${
        retryCount + 1
      }/${MAX_RETRIES}...`
    );
    setRetryCount((c) => c + 1);

    setTimeout(() => {
      videoEl.load();
      videoEl.play().catch(() => {});
    }, 2000);
  };

  // Reset retry count when video loads successfully
  const handleLoadedData = () => {
    setIsVideoReady(true);
    setRetryCount(0);
  };

  const onLoad = (data: OnLoadData) => {
    setDuration(data.duration);
    setIsVideoReady(true);
  };

  const onBuffer = ({ isBuffering }: OnBufferData) => {
    setIsBuffering(isBuffering);
  };

  const onProgress = (data: OnProgressData) => {
    setCurrentTime(data.currentTime);
  };

  const onError = (error: any) => {
    console.warn(`[VideoPlayer] Error loading video:`, error);

    if (retryCount >= MAX_RETRIES) {
      console.error('[VideoPlayer] Reached maximum retries.');
      return;
    }

    setRetryCount((c) => c + 1);

    setTimeout(() => {
      setSourceKey(`${clientId}-${Date.now()}`);
    }, 1000);
  };

  if (Platform.OS !== 'web') {
    return (
      <>
        {videoUrl && (
          <Video
            key={sourceKey}
            ref={video}
            source={{ uri: videoUrl }}
            style={{
              ...StyleSheet.absoluteFillObject,
              opacity: isVideoReady ? 1 : 0,
            }}
            controls={true}
            resizeMode='contain'
            onLoad={onLoad}
            onError={onError}
            paused={shouldPause}
            onBuffer={onBuffer}
            onProgress={onProgress}
            testID='video-component'
          />
        )}
        {(!videoUrl || isBuffering || !isVideoReady) && (
          <Box style={styles.loaderContainer}>
            <ActivityIndicator size='small' />
          </Box>
        )}
      </>
    );
  } else
    return (
      <div
        style={{
          position: 'relative',
          width: screenWidth,
          height: screenHeight ?? window.innerHeight - 293,
          backgroundColor: '#000',
          overflow: 'hidden',
        }}>
        {!isVideoReady && (
          <View
            style={{
              width: screenWidth,
              height: screenHeight ?? window.innerHeight - 293,
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: '#000',
            }}>
            <Image
              accessibilityLabel='Loading...'
              source={{ uri: thumbnail }}
              style={{
                width: screenWidth,
                height: screenHeight ?? window.innerHeight - 293,
              }}
              resizeMode='contain'
            />
            <ActivityIndicator
              size='large'
              color='#ffffff'
              style={{
                position: 'absolute',
              }}
            />
          </View>
        )}
        {videoUrl && (
          <video
            ref={localRef}
            controls
            autoPlay
            muted
            style={{
              width: screenWidth,
              height: screenHeight ?? window.innerHeight - 293,
              opacity: isVideoReady ? 1 : 0,
              transition: 'opacity 0.3s ease-in-out',
            }}
            src={videoUrl}
            onError={handleVideoError}
            onLoadedData={handleLoadedData}
          />
        )}
      </div>
    );
};

const styles = StyleSheet.create({
  videoContainer: {
    width: '100%',
    height: '50%',
  },
  loaderContainer: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: [{ translateX: -20 }, { translateY: -20 }],
  },
});
